import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CommonService from "../../services/common.service";
import StorageService from "../../services/StorageService";
import { API_URLS } from "../../constants/api-urls";
import "./PartyConnection.scss";

export default function PartyConnection() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [winningBid, setWinningBid] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [buyerProfile, setBuyerProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAuctionData();
  }, [auctionId]);

  const fetchAuctionData = async () => {
    try {
      setLoading(true);
      
      // Fetch auction details
      const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      if (auctionResponse && auctionResponse.success) {
        setAuction(auctionResponse.data);
        
        // Fetch winning bid to get buyer information
        try {
          const bidsResponse = await CommonService.get(API_URLS.AUCTION.BIDS(auctionId));
          if (bidsResponse && bidsResponse.success && bidsResponse.data) {
            const bids = Array.isArray(bidsResponse.data) ? bidsResponse.data : [];
            const winningBidData = bids.find(b => 
              b.bid_id === auctionResponse.data.winning_bid_id || 
              b.bid_status === 'won'
            );
            if (winningBidData) {
              setWinningBid(winningBidData);
              
              // Fetch buyer company profile using user_login_id
              // Note: The bid data already has bidder_company, bidder_email, bidder_phone, bidder_address from SQL JOIN
              // We'll try to fetch full profile, but use bidder_* fields as fallback
              if (winningBidData.user_login_id) {
                try {
                  // Try to get company profile by user_login_id
                  // First, try to get the user's company_id
                  let companyId = null;
                  try {
                    const userResponse = await CommonService.get(`/users/${winningBidData.user_login_id}`);
                    if (userResponse && userResponse.success && userResponse.data) {
                      companyId = userResponse.data.company_id;
                    }
                  } catch (userError) {
                    console.log("Could not fetch user data:", userError);
                  }
                  
                  // Try fetching profile by company_id if available
                  if (companyId) {
                    try {
                      const buyerProfileResponse = await CommonService.get(`/company/profile/${companyId}`);
                      if (buyerProfileResponse && buyerProfileResponse.success && buyerProfileResponse.data) {
                        setBuyerProfile(buyerProfileResponse.data);
                      }
                    } catch (profileError) {
                      console.log("Could not fetch profile by company_id, using bidder fields:", profileError);
                    }
                  }
                } catch (error) {
                  console.error("Error fetching buyer profile:", error);
                  // Will use bidder_* fields from winningBidData as fallback
                }
              }
            }
          }
        } catch (error) {
          console.error("Error fetching bids:", error);
        }
        
        // Fetch seller company profile
        if (auctionResponse.data.seller_company_id || auctionResponse.data.user_login_id) {
          try {
            const sellerProfileResponse = await CommonService.get(
              `/company/profile/${auctionResponse.data.seller_company_id || auctionResponse.data.user_login_id}`
            );
            if (sellerProfileResponse && sellerProfileResponse.success) {
              setSellerProfile(sellerProfileResponse.data);
            }
          } catch (error) {
            console.error("Error fetching seller profile:", error);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching auction data:", error);
      toast.error("Failed to load auction details");
    } finally {
      setLoading(false);
    }
  };

  const formatAddress = (addressData) => {
    if (!addressData) return "N/A";
    
    try {
      let address = typeof addressData === 'string' 
        ? JSON.parse(addressData) 
        : addressData;
      
      const locationParts = [];
      if (address.city) locationParts.push(address.city);
      if (address.state) locationParts.push(address.state);
      if (address.country && address.country !== "India") locationParts.push(address.country);
      
      return locationParts.length > 0 ? locationParts.join(", ") : "N/A";
    } catch (error) {
      return typeof addressData === 'string' ? addressData : "N/A";
    }
  };

  const handleProceedToInvoice = () => {
    navigate(`/auction/${auctionId}/proforma-invoice`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="party-connection-page">
        <div className="loading-container">Loading...</div>
      </div>
    );
  }

  if (!auction) {
    return (
      <div className="party-connection-page">
        <div className="error-container">Auction not found</div>
      </div>
    );
  }

  const currentUser = StorageService.getUser();
  
  // Check if user is a buyer (buyer role or both role)
  const isBuyer = () => {
    if (!currentUser) return false;
    
    // Check user_type_id: 1 = BUYER, 2 = SELLER, 3 = BOTH
    const userTypeId = String(currentUser.user_type_id || '').trim();
    const typeIds = userTypeId ? userTypeId.split(',').map(id => String(id).trim()) : [];
    
    // Check if user_type_id includes '1' (Buyer) or '3' (Both)
    const isBuyerByUserType = typeIds.includes('1') || 
                              typeIds.includes('3') || 
                              userTypeId === '1' || 
                              userTypeId === '3';
    
    // Also check company_role from user object
    const companyRole = currentUser.company_role || '';
    const isBuyerByRole = companyRole === 'BUYER' || companyRole === 'BOTH';
    
    return isBuyerByUserType || isBuyerByRole;
  };
  
  const isBuyerValue = isBuyer();

  // Seller details - from seller profile or auction data
  const sellerContactDetails = sellerProfile?.contact_details || {};
  const sellerCompany = sellerProfile?.company_name || auction.seller_company || auction.seller_name || "N/A";
  const sellerPhone = sellerContactDetails.contact_number || sellerProfile?.user_phone || auction.seller_phone || auction.seller_contact_phone || "+91 XX XXXX XXXX";
  const sellerEmail = sellerContactDetails.contact_email || sellerProfile?.user_email || auction.seller_email || auction.seller_contact_email || "N/A";
  const sellerLocation = formatAddress(sellerProfile?.address || auction.address || auction.seller_address);

  // Buyer details - from buyer profile, winning bid (with bidder_* fields), or auction data
  const buyerContactDetails = buyerProfile?.contact_details || {};
  const buyerCompany = buyerProfile?.company_name || 
                       winningBid?.bidder_company || 
                       winningBid?.buyer_company || 
                       auction.buyer_company || 
                       "N/A";
  const buyerPhone = buyerContactDetails.contact_number || 
                     buyerProfile?.user_phone || 
                     winningBid?.bidder_phone || 
                     winningBid?.buyer_phone || 
                     auction.buyer_phone || 
                     "+91 XX XXXX XXXX";
  const buyerEmail = buyerContactDetails.contact_email || 
                    buyerProfile?.user_email || 
                    winningBid?.bidder_email || 
                    winningBid?.buyer_email || 
                    auction.buyer_email || 
                    "N/A";
  const buyerLocation = formatAddress(
    buyerProfile?.address || 
    winningBid?.bidder_address || 
    winningBid?.buyer_address || 
    auction.buyer_address
  );

  return (
    <div className="party-connection-page">
      <div className="party-connection-container">
        <button className="back-btn" onClick={handleBack}>
          ← Back
        </button>

        {/* Header Section */}
        <div className="header-section">
          <div className="header-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className="header-content">
            <h1 className="page-title">Party Connection</h1>
            <p className="page-subtitle">
              {isBuyerValue ? "Seller contact details shared" : "Buyer contact details shared"}
            </p>
          </div>
        </div>

        {/* Details Cards */}
        <div className="details-cards">
          {/* Seller Details - Show only for buyers */}
          {isBuyerValue && (
          <div className="detail-card seller-card">
            <div className="card-header">
              <div className="card-icon seller-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <h2 className="card-title">Seller Details</h2>
            </div>
            <div className="card-body">
              <div className="info-row">
                <span className="info-label">Company:</span>
                <span className="info-value">{sellerCompany}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Phone Number:</span>
                <div className="info-value-with-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  <span>{sellerPhone}</span>
                </div>
              </div>
              <div className="info-row">
                <span className="info-label">Email Address:</span>
                <div className="info-value-with-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span>{sellerEmail}</span>
                </div>
              </div>
              <div className="info-row">
                <span className="info-label">Location:</span>
                <div className="info-value-with-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <span>{sellerLocation}</span>
                </div>
              </div>
            </div>
          </div>
          )}

          {/* Buyer Details - Show only for sellers */}
          {!isBuyerValue && (
          <div className="detail-card buyer-card">
            <div className="card-header">
              <div className="card-icon buyer-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
              <h2 className="card-title">Buyer Details</h2>
            </div>
            <div className="card-body">
              <div className="info-row">
                <span className="info-label">Company:</span>
                <span className="info-value">{buyerCompany}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Phone Number:</span>
                <div className="info-value-with-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                  <span>{buyerPhone}</span>
                </div>
              </div>
              <div className="info-row">
                <span className="info-label">Email Address:</span>
                <div className="info-value-with-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span>{buyerEmail}</span>
                </div>
              </div>
              <div className="info-row">
                <span className="info-label">Location:</span>
                <div className="info-value-with-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <span>{buyerLocation}</span>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Success Banner */}
        <div className="success-banner">
          <div className="success-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h3 className="success-title">✓ Parties Connected Successfully!</h3>
          <p className="success-description">
            Both buyer and seller can now communicate directly. Proceed to generate proforma invoice and arrange inspection.
          </p>
        </div>

        {/* Action Button - Show only for buyers */}
        {isBuyerValue && (
          <div className="action-section">
            <button className="btn-proceed" onClick={handleProceedToInvoice}>
              Proceed to Invoice
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
